/**
 * Phone navigation (< 768 px): the rail as an off-canvas drawer, opened from the
 * top bar's hamburger. Fly-outs become drill-down lists: tapping a Hub with items
 * shows its menu with a Back button; descriptions show inline (no hover on touch).
 * Esc or the backdrop closes it, and focus goes back to the hamburger.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import BrandIcon from '../assets/DWA_WolfHead_Favicon_Web.png';
import { DWA } from '../theme/dwaTokens';
import { HubMenu, PinnedMenu } from './HubMenu';
import { RowIcon } from './HubRail';
import { moveFocus, rowStyle, useRailEntries } from './railModel';
import type { RailEntry } from './railModel';

export const HubDrawer: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const entries = useRailEntries();
  const { pathname, search } = useLocation();
  const [view, setView] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const lastPath = useRef(pathname + search);

  // Close on navigation.
  useEffect(() => {
    const here = pathname + search;
    if (here !== lastPath.current) {
      lastPath.current = here;
      onClose();
    }
  }, [pathname, search, onClose]);

  useEffect(() => {
    if (!open) setView(null);
  }, [open]);

  // Focus the first entry on open and on every drill-down / back.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>('[data-drawer-focus]')?.focus();
  }, [open, view]);

  if (!open) return null;

  const current = entries.find((e): e is Extract<RailEntry, { kind: 'menu' }> => e.kind === 'menu' && e.key === view);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (current) setView(null);
      else onClose();
      return;
    }
    if (moveFocus(panelRef.current, '[data-rail-row], [data-menu-item], [data-drawer-nav]', e.key)) e.preventDefault();
    if (e.key === 'Tab') {
      // Keep focus inside the drawer while it's open.
      const els = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? []);
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200 }}>
      <div aria-hidden="true" onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        data-testid="hub-drawer"
        onKeyDown={onKeyDown}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 'min(300px, 86vw)',
          background: DWA.rail,
          color: DWA.onDark,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '8px 0 30px -10px rgba(0,0,0,0.6)',
          fontFamily: DWA.font,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 0.75rem 0.75rem 1rem', borderBottom: `1px solid ${DWA.railLine}` }}>
          <img src={BrandIcon} alt="" style={{ width: 26, height: 26, objectFit: 'cover' }} />
          <span style={{ fontWeight: 700, flex: 1 }}>Omega Wolf</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            style={{ width: 36, height: 36, border: 'none', background: 'transparent', color: DWA.onDark, cursor: 'pointer', borderRadius: 999 }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
          {current ? (
            <>
              <button
                type="button"
                data-drawer-focus
                data-drawer-nav
                onClick={() => setView(null)}
                className="ow-rail-row"
                style={{ ...rowStyle(false, false), color: DWA.onDarkMuted, fontSize: '0.85rem' }}
              >
                <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" style={{ width: 20 }} />
                All menus
              </button>
              <p style={{ margin: 0, padding: '0.5rem 0.75rem', fontWeight: 700, fontSize: '1rem' }}>{current.label}</p>
              {current.hub ? (
                <HubMenu hub={current.hub} variant="drawer" onNavigate={onClose} />
              ) : (
                <PinnedMenu variant="drawer" onNavigate={onClose} />
              )}
            </>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {entries.map((entry, i) => {
                if (entry.kind === 'divider') {
                  return <li key={entry.key} role="separator" aria-hidden="true" style={{ height: 1, background: DWA.railLine, margin: '0.5rem 0.4rem' }} />;
                }
                const focusProps = i === 0 ? { 'data-drawer-focus': true } : {};
                return (
                  <li key={entry.key} style={{ margin: '0.1rem 0' }}>
                    {entry.kind === 'link' ? (
                      <Link
                        {...focusProps}
                        data-rail-row
                        to={entry.to}
                        onClick={onClose}
                        aria-current={entry.active ? 'page' : undefined}
                        className="ow-rail-row"
                        style={rowStyle(entry.active, false)}
                      >
                        <RowIcon icon={entry.icon} active={entry.active} />
                        <span style={{ flex: 1 }}>{entry.label}</span>
                      </Link>
                    ) : (
                      <button
                        {...focusProps}
                        type="button"
                        data-rail-row
                        data-testid={`drawer-row-${entry.key}`}
                        onClick={() => setView(entry.key)}
                        className="ow-rail-row"
                        style={rowStyle(entry.active, false)}
                      >
                        <RowIcon icon={entry.icon} active={entry.active} />
                        <span style={{ flex: 1 }}>{entry.label}</span>
                        <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" style={{ fontSize: '0.65rem', color: 'rgba(248,250,252,0.5)' }} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
